import clsx from 'clsx'
import { FC, useState } from 'react'
import styles from './CommentHubAvatar.module.css'

export interface CommentHubAvatarProps {
  src?: string | null
  /** The name the initials come from — the caller resolves its own anonymous fallback. */
  name: string
  /** One letter (comment surfaces) or up to two, one per word (the profile header). */
  initials?: 1 | 2
  /**
   * The comment surfaces' shared sizes, styled here. Omit it when the caller sizes the
   * avatar itself through `className` (the tray's stacked faces, the profile header).
   */
  size?: 32 | 40
  /** Inline fallback background (`avatarColor(id)`); without it the size's CSS tint applies. */
  color?: string
  alt?: string
  lazy?: boolean
  /** Applied to the image and to the fallback element. */
  className?: string
  fallbackClassName?: string
  /** `'text'` renders the bare initials, for a caller that owns the wrapping element. */
  fallback?: 'div' | 'span' | 'text'
}

function initialsOf(name: string, count: 1 | 2): string {
  if (count === 1) {
    return name.charAt(0).toUpperCase()
  }
  return name
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map(w => (w[0] ?? '').toUpperCase())
    .join('')
}

/**
 * The avatar-with-initials fallback every commentHub surface renders.
 *
 * Avatar urls are third-party (Google serves them from lh3.googleusercontent.com) and fail
 * transiently: the `=s96-c` sizing suffix is an on-the-fly resize endpoint that answers 429
 * under load, and provider urls rotate. Falling back to the initials keeps a broken-image
 * icon off the page — the url being present is not the same as the image being loadable.
 *
 * `referrerPolicy="no-referrer"` on every image (these are Google avatar urls): the profile
 * header always sent none, Google's avatar host is known to reject some hotlinked requests
 * that carry a Referer, and it has no business seeing which article the reader is on.
 */
const CommentHubAvatar: FC<CommentHubAvatarProps> = ({
  src,
  name,
  initials = 1,
  size,
  color,
  alt = '',
  lazy = true,
  className,
  fallbackClassName,
  fallback = 'div',
}) => {
  const [failed, setFailed] = useState(false)
  const sized = size ? styles[`s${size}`] : undefined

  if (src && !failed) {
    return (
      <img
        className={clsx(size && styles.avatar, sized, className) || undefined}
        src={src}
        alt={alt}
        loading={lazy ? 'lazy' : undefined}
        referrerPolicy="no-referrer"
        onError={() => {
          setFailed(true)
        }}
      />
    )
  }

  const letters = initialsOf(name, initials)
  if (fallback === 'text') {
    // The fragment is what lets an FC return bare text — the caller owns the wrapper.
    // eslint-disable-next-line react/jsx-no-useless-fragment
    return <>{letters}</>
  }
  const Tag = fallback
  return (
    <Tag
      className={
        clsx(size && [styles.avatar, styles.fallback], sized, className, fallbackClassName) ||
        undefined
      }
      style={color ? { backgroundColor: color } : undefined}
      aria-hidden="true"
    >
      {letters}
    </Tag>
  )
}

export default CommentHubAvatar
